import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

export const PropertyHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const heroImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/evbm1o7q_F%20%281%20of%2032%29.jpg',
      alt: 'Modern living room with floor-to-ceiling windows and patio access'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/cecf0pum_F%20%282%20of%2032%29.jpg',
      alt: 'Open plan living and dining with Scandinavian design'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/16nfcyq3_F%20%286%20of%2032%29.jpg',
      alt: 'Bright living area with designer plant shelves'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/r86ciw9y_F%20%2825%20of%2032%29.jpg',
      alt: 'Living space with terracotta pendant and patio access'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/7h8khywr_F%20%2831%20of%2032%29.jpg',
      alt: 'Stunning twilight exterior view with warm interior lighting'
    }
  ];

  // Preload first image for faster LCP
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = heroImages[0].url;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  
  const scrollToNextSection = () => {
    const nextSection = document.getElementById('overview');
    nextSection?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
      setTimeout(() => setIsTransitioning(false), 700);
    }
  };
  
  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
      setTimeout(() => setIsTransitioning(false), 700);
    }
  };
  
  const goToSlide = (index) => {
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 700);
    }
  };
  
  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentSlide]);
  
  return (
    <section className="relative h-screen w-full overflow-hidden group">
      {/* Hero Image Slideshow - Clean, no overlays */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img 
              src={image.url}
              alt={image.alt}
              className="h-full w-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              width={1920}
              height={1080}
              fetchpriority={index === 0 ? 'high' : 'auto'}
            />
          </div>
        ))}
      </div>
      
      {/* Slideshow Navigation - Minimal, elegant */}
      <div className="absolute inset-0 z-30 flex items-center justify-between px-6 sm:px-10 pointer-events-none">
        <button
          onClick={prevSlide}
          className="pointer-events-auto p-2.5 rounded-full bg-background/90 backdrop-blur-md text-foreground hover:bg-background transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 shadow-lg"
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} strokeWidth={2.5} />
        </button>
        <button
          onClick={nextSlide}
          className="pointer-events-auto p-2.5 rounded-full bg-background/90 backdrop-blur-md text-foreground hover:bg-background transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 shadow-lg"
          aria-label="Next slide"
        >
          <ChevronRight size={20} strokeWidth={2.5} />
        </button>
      </div>
      
      {/* Slide Indicators - Bottom center */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              index === currentSlide 
                ? 'w-8 bg-background shadow-md' 
                : 'w-1.5 bg-background/50 hover:bg-background/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Scroll Indicator - Subtle */}
      <button 
        onClick={scrollToNextSection}
        className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 text-background/70 hover:text-background transition-all duration-300 animate-bounce"
        aria-label="Scroll to explore"
      >
        <ChevronDown size={28} strokeWidth={2} />
      </button>
    </section>
  );
};